import { useEffect, useMemo, useRef, useState } from "react";
import { einheitFuer, useT } from "../i18n";
import { alsZahl } from "./index";
import { Zahlzeile } from "./Postendetails";
import { useLeistungsarten } from "../daten/haken";
import { leistungsmenge, leistungssatz } from "../daten/leistungssatz";
import { useLetzteMenge, type Mengenvorschlag } from "../daten/letztemenge";
import { quellenwort } from "../daten/mengenvorschlag";
import "../stile/leistungsmenge.css";
import { istPauschale } from "../daten/einmalbetrag";
import { tagKurz } from "../daten/nachschlagen";
import { postenAendern, useMerkliste, type Merkposten, type Teilposten } from "../daten/merkliste";

/* ══════════════════════════════════════════════════════════════════
   Die Menge einer Leistung, direkt am Posten der Merkliste (08.10.2026).

   Luca: *„Ich will bei der PV-Schnellvermietung, also bei PV-Reinigung als
   Leistung, auch direkt die Quadratmeterzahl eintragen können, sodass ich
   das auch direkt vermieten kann. … Guck, ob es das letzte Mal schon mal
   gab, das vorschlagen, aber man kann das auch ohne Ort vermieten."*

   **Es hängt an „Leistung mit Mengeneinheit", nicht am Namen.** m², Stunden,
   Module, Stück — jede Leistung, deren Einheit keine Pauschale ist, trägt das
   Feld. Leer heißt an einer Leistung mit Aufmaß wie bisher „Menge folgt aus
   dem Aufmaß" (0); jede andere behält mindestens eins.

   **Gerechnet wird nicht hier.** `leistungsmenge` (`daten/leistungssatz.ts`)
   fragt `fach.mengenpostenRechnen` — Mindestmenge, dann Mengenstufe, wie die
   API die Position anlegt. Der Posten trägt danach die angehobene Menge und
   den Satz ihrer Stufe; Betrag und Summe folgen von selbst (`postenSumme`).

   **Der Vorschlag wird gezeigt, nicht eingetragen** (Regel 11). Er steht als
   Platzhalter im leeren Feld; übernommen wird er erst mit Tab, Enter oder
   einem Klick ins Feld. Eine Zahl, die still im Feld stünde, ginge als
   vereinbarte Menge hinaus, ohne dass jemand sie gesagt hat.

   Zwei Fassungen, ein Zustand: Am Schreibtisch steht das Feld in den
   Einstellungen der aufgeklappten Zeile (`Leistungsfelder`), am Telefon im Blatt zum Posten (`Postenblatt.tsx`) — beide
   nehmen `useLeistungsmenge`.
   ══════════════════════════════════════════════════════════════════ */

/** Trägt dieser Posten das Mengenfeld? Eine Leistung mit Mengeneinheit. */
export function mitMengenfeld(p: Merkposten): boolean {
  return p.art === "leistung" && Boolean(p.einheitKey) && !istPauschale(p.einheitKey);
}

/** Wartet die Leistung noch auf das Aufmaß? Dann ist ihr Feld leer. */
export function mengeFolgtAusAufmass(p: Merkposten): boolean {
  return p.art === "leistung" && p.aufmass === true && p.menge === 0;
}

/** Die Menge im Feld — ohne Tausenderpunkt, sonst läse `alsZahl` „1.200" als 1,2. */
function feldtext(p: Merkposten): string {
  return mengeFolgtAusAufmass(p) ? "" : String(p.menge).replace(".", ",");
}

/** Die Menge im Satz — „1.200", „580,5". */
export function mengentext(menge: number): string {
  return menge.toLocaleString("de-DE", { maximumFractionDigits: 2 });
}

export function useLeistungsmenge(p: Merkposten) {
  const t = useT();
  const { wer } = useMerkliste();
  const { data: leistungsarten } = useLeistungsarten();
  const leistung = useMemo(
    () => (p.leistungsartId ? (leistungsarten?.zeilen ?? []).find((l) => l.id === p.leistungsartId) ?? null : null),
    [leistungsarten, p.leistungsartId],
  );
  const leerErlaubt = p.aufmass === true;

  /* Das Feld trägt den Text und nicht die Zahl — wer „580," tippt, soll nicht
     zusehen, wie daraus 580 wird, bevor er fertig ist. */
  const stand = feldtext(p);
  const [feld, setzeFeld] = useState(stand);
  useEffect(() => { setzeFeld(stand); }, [p.id, stand]);

  /* Für wen gemerkt wurde, sonst wer oben steht (`gehoertZu`). Den Ort gibt es
     nur oben — ein Posten trägt keinen eigenen. */
  const kunde = p.kunde || wer.nr;
  const abfrage = useLetzteMenge(
    { kunde, leistungsart: p.leistungsartId ?? "", ort: !p.kunde || p.kunde === wer.nr ? wer.ort : "" },
    mitMengenfeld(p),
  );
  /** Der Vorschlag — nur, solange das Feld leer ist: Dann steht er als Platzhalter darin. */
  const vorschlag: Mengenvorschlag | null = abfrage.data?.vorschlag && mengeFolgtAusAufmass(p) ? abfrage.data.vorschlag : null;
  /** Alle früheren Werte (Paket aufnahmen2) — der erste ist der Vorschlag. */
  const frueher = abfrage.data?.frueher ?? [];
  /** Woher die Zahl im Feld kam, wenn sie aus einem früheren Wert übernommen wurde. */
  const [ausWert, setzeAusWert] = useState<Mengenvorschlag | null>(null);
  useEffect(() => { setzeAusWert(null); }, [p.id]);

  /* **Der Grad, wo die Leistung mehrere führt** (P8-05) — dieselbe Wahl wie
     im Abschluss. */
  const satz = leistung ? leistungssatz(leistung, p.kategorieId ?? null, p.menge) : null;
  const grade = satz && satz.grade.length > 1 ? satz.grade : [];

  /**
   * **Mit eingetragener Menge ist nichts weiter nötig** (Luca, 08.10.2026) —
   * auch kein Grad. Steht keiner fest (keiner gewählt, keine Vorgabe, nicht
   * der einzige), nimmt der Posten den Grad vom letzten Mal, sonst den ersten
   * der Leistungsart. Er steht danach leise im Feld daneben und lässt sich
   * ändern; „Jetzt vermieten" hält nicht mehr an.
   */
  const gradFuerMenge = (): string | null => {
    if (!leistung || p.kategorieId || !satz?.wahlNoetig) return p.kategorieId ?? null;
    const zuletzt = abfrage.data?.vorschlag?.kategorieId;
    if (zuletzt && satz.grade.some((g) => g.id === zuletzt)) return zuletzt;
    return satz.grade[0]?.id ?? null;
  };

  const schreiben = (eingabe: number, grad?: string | null) => {
    const kategorie = grad ?? (eingabe > 0 ? gradFuerMenge() : (p.kategorieId ?? null));
    const r = leistungsmenge(leistung, kategorie, eingabe, leerErlaubt);
    const teil: Teilposten = { menge: r.menge };
    /* Mit Stamm: der Satz der Stufe dieser Menge (Staffel). Ohne Stamm — eine
       Zeile von Hand — bleibt ihr Satz, wie er dasteht. Ein Satz von Hand
       (`einzelpreis`) bleibt ohnehin: Er schlägt den gerechneten. */
    if (leistung) teil.netto = r.satz ?? 0;
    if (kategorie && kategorie !== p.kategorieId) teil.kategorieId = kategorie;
    postenAendern(p.id, teil);
    setzeFeld(mengeFolgtAusAufmass({ ...p, menge: r.menge }) ? "" : String(r.menge).replace(".", ","));
  };

  /** Den Vorschlag übernehmen — der eine bewusste Griff (Tab, Enter, Antippen). */
  const gerade = useRef(false);
  const uebernehmen = (): boolean => {
    if (!vorschlag || feld.trim()) return false;
    /* Nur das Verlassen, das gleich auf Tab oder Enter folgt, liest noch das
       leere Feld. Nach einem Klick bleibt das Feld im Fokus — dann gilt die
       Marke nicht für ein späteres Verlassen. */
    gerade.current = true;
    window.setTimeout(() => { gerade.current = false; }, 0);
    schreiben(vorschlag.menge);
    setzeAusWert(vorschlag);
    return true;
  };

  /**
   * **Einen früheren Wert übernehmen** (Paket aufnahmen2) — die Menge und den
   * Grad, falls er damals erfasst wurde und die Leistung ihn führt. Keine
   * Verknüpfung mit dem Auftrag von damals: Es ist nur die Zahl.
   */
  const wertWaehlen = (w: Mengenvorschlag) => {
    const grad = w.kategorieId && satz?.grade.some((g) => g.id === w.kategorieId) ? w.kategorieId : undefined;
    schreiben(w.menge, grad);
    setzeAusWert(w);
  };

  /** Beim Verlassen: leer heißt „folgt aus dem Aufmaß" — oder zurück zum alten Wert. */
  const fertig = (text: string) => {
    // Eben übernommen: Das Verlassen danach liest noch das leere Feld.
    if (gerade.current) { gerade.current = false; return; }
    if (!text.trim()) {
      if (leerErlaubt) { if (p.menge !== 0) schreiben(0); else setzeFeld(""); return; }
      setzeFeld(feldtext(p));
      return;
    }
    const zahl = alsZahl(text);
    if (zahl === undefined || zahl < 0 || (zahl === 0 && !leerErlaubt)) { setzeFeld(feldtext(p)); return; }
    if (zahl === p.menge) { setzeFeld(feldtext(p)); return; }
    schreiben(zahl);
    setzeAusWert(null);
  };

  const einheitKey = p.einheitKey ?? "";
  const einheit = einheitKey ? t(einheitKey) : "";
  const mitEinheit = (menge: number) =>
    t("merkliste.mengeEinheit", { zahl: mengentext(menge), einheit: einheitFuer(t, einheitKey, menge) });

  /** „zuletzt 580 m² · V-2026-0002" — der `title` am Feld, wenn der Platzhalter der Vorschlag ist. */
  const vorschlagText = vorschlag
    ? t(vorschlag.nr ? "merkliste.mengeZuletztTitel" : "merkliste.mengeZuletztAufmassTitel", {
      menge: mitEinheit(vorschlag.menge),
      nr: vorschlag.nr,
      datum: vorschlag.datum ? tagKurz(vorschlag.datum) : "",
    })
    : "";
  /** Im leeren Feld: die Menge vom letzten Mal, sonst „Aufmaß". */
  const platz = vorschlag ? mengentext(vorschlag.menge) : leerErlaubt ? t("merkliste.mengePlatzAufmass") : "";

  /** Die Mindestmenge hat angehoben — die Position wird so stehen (E10). */
  const mindest = leistung?.mindest_menge ?? null;
  const mindestText = mindest !== null && mindest > 0 && p.menge > 0 && p.menge === mindest
    ? t("positionen.mindestmenge", { menge: mitEinheit(mindest) })
    : "";

  const gradWert = p.kategorieId ?? satz?.kategorieId ?? "";
  const gradSetzen = (neu: string | null) => {
    if (!leistung) return;
    const jetzt = leistungssatz(leistung, neu, p.menge);
    postenAendern(p.id, { kategorieId: neu ?? undefined, netto: jetzt.satz ?? 0, einzelpreis: undefined });
  };

  /** Die Quelle leise am Feld: die des Vorschlags im leeren Feld, sonst die des übernommenen Werts. */
  const quelle = vorschlag ? quellenwort(vorschlag, t, tagKurz)
    : ausWert && ausWert.menge === p.menge ? quellenwort(ausWert, t, tagKurz) : "";
  const wertZeile = (w: Mengenvorschlag) => [
    w.datum ? tagKurz(w.datum) : "", w.ort || t("merkliste.mengeOhneOrt"), mitEinheit(w.menge),
    w.nr ? w.nr : t("merkliste.mengeQuelleAufnahme"),
  ].filter(Boolean).join(` ${t("allgemein.trennpunkt")} `);

  return {
    quelle,
    frueher,
    wertWaehlen,
    wertZeile,
    grade,
    gradWert,
    // Rot nur ohne Menge: Mit Menge steht der Grad schon da (`gradFuerMenge`).
    gradNoetig: satz?.wahlNoetig === true && p.menge === 0,
    gradSetzen,
    feld,
    setzeFeld,
    fertig,
    vorschlag,
    vorschlagText,
    platz,
    uebernehmen,
    einheit,
    mindestText,
    leerErlaubt,
  };
}

/**
 * Menge und Grad als Zellen im Raster der Einstellungen (Schreibtisch,
 * `Postenaufstellung.tsx`) — aufgeklappt an der Zeile der Merkliste und im
 * Abschluss. Felder 40 px wie jedes Feld; der Vorschlag vom letzten Mal steht
 * nur als Platzhalter im Feld („580", `title` „zuletzt 580 m² · V-2026-0002")
 * — Luca, 08.10.2026: kein langer Satz. Tab, Enter oder ein Klick ins Feld
 * übernehmen ihn.
 */
export function Leistungsfelder({ p }: { p: Merkposten }) {
  const t = useT();
  const m = useLeistungsmenge(p);
  const [wahlOffen, setzeWahlOffen] = useState(false);
  const feld = mitMengenfeld(p);
  if (!feld && !m.grade.length) return null;
  return (
    <>
      {feld ? (
        <label className="pst-zelle">
          <span className="pst-schild">{t("angebotBauen.spalteMenge")}</span>
          <span className="pst-mit-einheit">
            <input
              className="feld pst-feld-zahl"
              inputMode="decimal"
              autoComplete="off"
              enterKeyHint="done"
              aria-label={m.einheit ? t("merkliste.mengeSchild", { einheit: m.einheit }) : t("angebotBauen.spalteMenge")}
              placeholder={m.platz}
              title={m.vorschlag ? m.vorschlagText : m.leerErlaubt && !m.feld ? t("positionen.mengeAufmass") : undefined}
              value={m.feld}
              onChange={(e) => m.setzeFeld(e.target.value)}
              onBlur={(e) => m.fertig(e.target.value)}
              /* Einmal hineinklicken übernimmt den Vorschlag; sonst bleibt das Feld leer. */
              onClick={() => { m.uebernehmen(); }}
              onKeyDown={(e) => {
                if (e.key === "Tab") m.uebernehmen();
                if (e.key === "Enter") { m.uebernehmen(); e.currentTarget.blur(); }
              }}
            />
            {m.einheit ? <span className="pst-einheit">{m.einheit}</span> : null}
          </span>
          {m.mindestText ? <span className="pst-hinweis">{m.mindestText}</span> : null}
          {/* Die Quelle leise, und mehrere frühere Werte zur Auswahl (Paket aufnahmen2). */}
          {m.quelle || m.frueher.length > 1 ? (
            <span className="pst-hinweis lm-quelle">
              {m.quelle}
              {m.frueher.length > 1 ? (
                <button
                  type="button"
                  className="lm-verweis"
                  aria-expanded={wahlOffen}
                  onClick={(e) => { e.preventDefault(); setzeWahlOffen((o) => !o); }}
                >
                  {t("merkliste.mengeFrueher", { zahl: m.frueher.length })}
                </button>
              ) : null}
            </span>
          ) : null}
          {wahlOffen ? (
            <span className="lm-wahl" role="group" aria-label={t("merkliste.mengeFrueherSchild")}
              onKeyDown={(e) => { if (e.key === "Escape") setzeWahlOffen(false); }}>
              {m.frueher.map((w, i) => (
                <button
                  key={`${w.nr ?? "a"}-${w.datum}-${i}`}
                  type="button"
                  className="lm-wert"
                  onClick={(e) => { e.preventDefault(); m.wertWaehlen(w); setzeWahlOffen(false); }}
                >
                  {m.wertZeile(w)}
                </button>
              ))}
            </span>
          ) : null}
        </label>
      ) : null}
      {m.grade.length ? (
        <label className="pst-zelle">
          <span className="pst-schild">{t("merkliste.gradSchild")}</span>
          <select
            /* Nicht rot, solange niemand abschließt (08.10.2026, Luca): Beim
               Stöbern ist ein offener Grad kein Fehler. Fehlt er beim
               Vermieten, sagt es der Abschluss (`MerklisteAbschluss`). */
            className="feld"
            aria-label={t("merkliste.gradSchild")}
            title={m.grade.find((g) => g.id === m.gradWert)?.label}
            value={m.gradWert}
            onChange={(e) => m.gradSetzen(e.target.value || null)}
          >
            {m.gradNoetig ? <option value="">{t("merkliste.gradWaehlen")}</option> : null}
            {m.grade.map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}
          </select>
        </label>
      ) : null}
    </>
  );
}

/**
 * Dieselbe Menge im Blatt zum Posten (Telefon) — die Zahlzeile der
 * Handyfassung (Feld 44 px, Schrift 16 px, `inputMode="decimal"`). Der
 * Vorschlag steht als Platzhalter im Feld und leise darunter; einmal antippen
 * übernimmt ihn.
 */
export function Mengenzeile({ p }: { p: Merkposten }) {
  const t = useT();
  const m = useLeistungsmenge(p);
  return (
    <>
      <Zahlzeile
        was={t("angebotBauen.spalteMenge")}
        dazu={m.mindestText || m.vorschlagText || (m.leerErlaubt && !m.feld ? t("positionen.mengeAufmass") : undefined)}
        wert={m.feld}
        platz={m.platz || undefined}
        beimAntippen={() => { m.uebernehmen(); }}
        einheit={m.einheit || undefined}
        schild={m.einheit ? t("merkliste.mengeSchild", { einheit: m.einheit }) : undefined}
        aendern={m.setzeFeld}
        fertig={m.fertig}
      />
    </>
  );
}
